import { NetworkError } from "../errors/NetworkError";

import { isRetryableResponseStatus } from "./retryableStatuses";
import type { HttpRetryOptions } from "./types";
import { DEFAULT_HTTP_RETRY_OPTIONS } from "./types";

export type NormalizedHttpRetryOptions = HttpRetryOptions | null;

/**
 * Convert per-request retry flag or overrides into concrete options or disabled retry.
 */
export function normalizeHttpRetryOptions(
  retry: boolean | Partial<HttpRetryOptions> | undefined,
  defaults: HttpRetryOptions = DEFAULT_HTTP_RETRY_OPTIONS,
): NormalizedHttpRetryOptions {
  if (retry === false) {
    return null;
  }

  if (retry === true || retry === undefined) {
    return defaults;
  }

  return {
    maxAttempts: retry.maxAttempts ?? defaults.maxAttempts,
    baseDelayMs: retry.baseDelayMs ?? defaults.baseDelayMs,
    backoffMultiplier: retry.backoffMultiplier ?? defaults.backoffMultiplier,
    jitterMs: retry.jitterMs ?? defaults.jitterMs,
  };
}

function getRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof URL) {
    return input.href;
  }
  return input.url;
}

function getRetryDelayMs(options: HttpRetryOptions, attempt: number): number {
  const exponentialDelay = options.baseDelayMs * options.backoffMultiplier ** (attempt - 1);
  const jitter = Math.floor(Math.random() * (options.jitterMs + 1));
  return exponentialDelay + jitter;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Appended to fetch error messages so PostgREST preserves the request URL (it only forwards name/message). */
export const SUPABASE_FETCH_REQUEST_URL_TAG = "[supabase_request_url:" as const;

function appendFetchRequestUrlTag(message: string, url: string): string {
  const encoded = encodeURIComponent(url);
  return `${message} ${SUPABASE_FETCH_REQUEST_URL_TAG}${encoded}]`;
}

function wrapFetchFailure(error: unknown, input: RequestInfo | URL): NetworkError {
  const url = getRequestUrl(input);
  const baseMessage =
    error instanceof Error ? error.message : "Network request failed";
  const message = baseMessage.includes(SUPABASE_FETCH_REQUEST_URL_TAG)
    ? baseMessage
    : appendFetchRequestUrlTag(baseMessage, url);
  const cause = error instanceof NetworkError ? (error.cause ?? error) : error;
  return new NetworkError(message, { cause, url });
}

/**
 * Fetch with retries on network failures and retryable HTTP statuses.
 */
export async function fetchWithRetryPolicy(
  input: RequestInfo | URL,
  init: RequestInit,
  retryOptions: NormalizedHttpRetryOptions,
): Promise<Response> {
  if (!retryOptions) {
    return fetch(input, init);
  }

  let lastError: unknown;

  for (let attempt = 1; attempt <= retryOptions.maxAttempts; attempt += 1) {
    try {
      const response = await fetch(input, init);
      if (response.ok || !isRetryableResponseStatus(response.status) || attempt === retryOptions.maxAttempts) {
        return response;
      }
    } catch (error) {
      lastError = error;
      if (attempt === retryOptions.maxAttempts) {
        throw wrapFetchFailure(error, input);
      }
    }

    const delay = getRetryDelayMs(retryOptions, attempt);
    await sleep(delay);
  }

  throw wrapFetchFailure(
    lastError instanceof Error ? lastError : new Error("Request failed after retry attempts"),
    input,
  );
}

/**
 * Returns a `fetch`-compatible function pinned to default retry options (e.g. Supabase global.fetch).
 */
export function createFetchWithRetry(defaults: HttpRetryOptions = DEFAULT_HTTP_RETRY_OPTIONS) {
  return (input: RequestInfo | URL, init?: RequestInit): Promise<Response> =>
    fetchWithRetryPolicy(input, init ?? {}, defaults);
}
